/// <reference types="react-dom" />
import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Link } from "OfficeFabric/Link";
import { MessageBar, MessageBarType } from "OfficeFabric/MessageBar";
import { ITextField, TextField } from "OfficeFabric/TextField";
import { getId } from "OfficeFabric/Utilities";
import { css } from "VSSUI/Util";
import { PolicyEvaluationStatus } from "Policy/Scripts/Generated/TFS.Policy.Contracts";
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import * as React from "react";
import { ISourceBranchUnmergedWarning } from "Scenarios/PullRequestDetail/Stores/PullRequestCompleteMergeStore";
import { GitPullRequestCompletionOptions, GitPullRequestMergeStrategy } from "TFS/VersionControl/Contracts";
import { ClientPolicyEvaluation } from "VersionControl/Scenarios/PullRequestDetail/Policy/ClientPolicyEvaluation";
import { IMergePolicySetting } from "VersionControl/Scenarios/PullRequestDetail/Stores/ClientPolicyEvaluationStore";
import { Flux } from "VersionControl/Scenarios/PullRequestDetail/View/PullRequestDetail.Flux";
import { AdditionalInfoTooltip } from "VersionControl/Scenarios/Shared/AdditionalInfoTooltip";
import { CompletionAnimation } from "VersionControl/Scripts/Components/PullRequestReview/CompletionAnimations";
import { MergeStrategyDropdown } from "VersionControl/Scripts/Components/PullRequestReview/MergeStrategyDropdown";
import { mergeStrategyDetails } from "VersionControl/Scripts/MergeStrategyDetails";
import * as VCResources from "VersionControl/Scripts/Resources/TFS.Resources.VersionControl";
import { WorkItemTransitionWarning } from "VersionControl/Scripts/Stores/PullRequestReview/RelatedWorkItemsStore";
import { FormattedComponent } from "VersionControl/Scripts/Utils/Format";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { format } from "VSS/Utils/String";
import { Checkbox } from "VSSUI/Checkbox";
import { DirectionalHint, TooltipHost, TooltipOverflowMode } from "VSSUI/Tooltip";
import "VSS/LoaderPlugins/Css!VersionControl/CompleteMergeBase";

export interface ICompleteMergeBaseProps {
    autoComplete: boolean;
    isOpen: boolean;
    transitionWorkItemsIsEnabled?: boolean;
    onResult(completionOptions?: GitPullRequestCompletionOptions): void;
}

export interface ICompleteMergeBaseState {
    mergePolicySetting: IMergePolicySetting;
    policyWarning: string;
    shouldSquashMerge: boolean;
    sourceBranchWillNotBeMergedWarning: ISourceBranchUnmergedWarning;
    workItemTransitionWarning: WorkItemTransitionWarning;
    shouldDeleteSourceBranch: boolean;
    shouldTransitionWorkItems: boolean;
    canTransitionWorkItems: boolean;
    canRebaseSourceBranch: boolean;
    cannotRebaseReasonHint: string;
    canDeleteSourceBranch: boolean;
    cannotDeleteReasonHint: string;
    sourceFriendlyName: string;
    shouldBypassPolicy: boolean;
    mergeTitle: string;
    mergeDescription: string;
    mergeDescriptionError: string;
    loading: boolean;
    canBypassPolicy: boolean;
    bypassReason: string;
    moveFocusToBypassReason: boolean;
    mergeStrategy: GitPullRequestMergeStrategy;
    animationSequence: number;
    someMergeStrategiesDisallowed: boolean;
    isSelectedMergeStrategyAllowed: boolean;
    completionOptions: GitPullRequestCompletionOptions;
    transitionWorkItemsHint: string;
}

export abstract class CompleteMergeBase extends React.Component<ICompleteMergeBaseProps, ICompleteMergeBaseState> {

    constructor(props: ICompleteMergeBaseProps) {
        super(props);
        this.state = this._getState();
    }

    protected _renderMainContent(): JSX.Element {
        return (
            <div className="form-section">
                {[
                    // If squashing but not deleting source, tell the user this is a bad idea
                    this._renderSourceBranchWillNotBeMergedWarningControls(),

                    // Warn if workitems can't be transitioned
                    this._renderWorkItemTransitionWarningControls(),

                    // Warn if policies not passing (but not for autocomplete, which is going to wait for them to pass)
                    this._renderPolicyWarningControls(),

                    // Warn if conflicting merge policies set at different scopes result in no merge type being legal
                    this._renderNoLegalMergesWarningControls(),
                ]}

                <div className="form-fields" key="fields">
                    {[
                        // Text fields for title and description
                        <div key="s1" className="section-header">{VCResources.PullRequest_CompleteMergeDialog_CommitComment}</div>,

                        this._renderMergeTitleControls(),
                        this._renderMergeDetailsControls(),

                        // Note which explains that commit messages aren't used for rebase
                        this._renderRebaseCommitMessageWarningControls(),

                        (this.state.mergePolicySetting.advancedMergesEnabled &&
                            this._renderAdvancedMergeUi()
                        ),

                        <div key="s2" className="section-header">{VCResources.PullRequest_CompleteMergeDialog_PostCompletion}</div>,

                        // Checkbox asking if user wants to transition (close) linked workitems
                        this._renderTransitionWorkItemsControls(),

                        // Checkbox asking if user wants to delete source branch after completion
                        this._renderDeleteSourceControls(),

                        (this.state.mergePolicySetting.advancedMergesEnabled ||
                            // Checkbox asking if user wants to squash commits
                            this._renderSquashControls()),

                        // If policies are failing but user can bypass, add a bypass checkbox and a reason text field
                        this._renderBypassControls(),
                    ]}
                </div>
            </div>
        );
    }

    protected _renderFooterContent = (): JSX.Element => {
        return <span key="footer" className="vc-complete-merge-footer">
            <PrimaryButton
                className={css(
                    "vc-complete-merge-button",
                    this._arePolicyBypassControlsVisible && this.state.shouldBypassPolicy && this._isCompleteButtonEnabled && "override-warning",
                )}
                onClick={this._onSave}
                disabled={!this._isCompleteButtonEnabled}
            >
                {this._completeButtonText}
            </PrimaryButton>

            <DefaultButton
                className="vc-complete-merge-button"
                onClick={this._onCancel}
            >
                {VCResources.PullRequest_CompleteMergeDialog_CancelText}
            </DefaultButton>
        </span>;
    }

    private _renderSourceBranchWillNotBeMergedWarningControls(): JSX.Element {
        const warning = this.state.sourceBranchWillNotBeMergedWarning;

        return (warning &&
            <div key="sourceBranchWillNotBeMergedWarning" className="vc-dialog-merge-warningarea">
                <MessageBar messageBarType={MessageBarType.warning}>
                {
                    !warning.reason ?
                        warning.message :
                        [warning.message, <br key="br" />, format(VCResources.Reason, warning.reason)]
                }
                </MessageBar>
            </div>
        );
    }

    private _renderWorkItemTransitionWarningControls(): JSX.Element {
        return (this.state.workItemTransitionWarning && this.state.shouldTransitionWorkItems &&
            <div key="workItemTransitionWarning" className="vc-dialog-merge-warningarea">
                <MessageBar messageBarType={MessageBarType.warning}>
                    <FormattedComponent
                        format={VCResources.PullRequest_CompleteMergeDialog_WorkItemTransitionWarningFormat}
                        className={"work-item-transition-warning"}>
                        {[
                            this.state.workItemTransitionWarning.message,
                            this.state.workItemTransitionWarning.link &&
                            <Link
                                key="learnMore"
                                className="work-item-transition-link"
                                href={this.state.workItemTransitionWarning.link}
                                target="_blank"
                                rel="noopener noreferrer">
                                {VCResources.PullRequest_CompleteMergeDialog_WorkItemTransitionWarningLearnMore}
                            </Link>,
                            this.state.workItemTransitionWarning.overflow,
                        ]}
                    </FormattedComponent>
                </MessageBar>
            </div>
        );
    }

    private _renderPolicyWarningControls(): JSX.Element {
        return (this.state.policyWarning && !this.props.autoComplete &&
            <div key="policyWarning" className="vc-dialog-merge-warningarea">
                <MessageBar messageBarType={MessageBarType.severeWarning}>
                    {this.state.policyWarning}
                </MessageBar>
            </div>
        );
    }

    private _renderNoLegalMergesWarningControls(): JSX.Element {
        return (this.state.mergePolicySetting.noLegalMerges &&
            <div key="noLegalMergesWarning" className="vc-dialog-merge-warningarea">
                <MessageBar messageBarType={MessageBarType.severeWarning}>
                    {VCResources.PullRequest_CompleteMergeDialog_NoLegalMerges}
                    <Link
                        key="learnMore"
                        href="https://go.microsoft.com/fwlink/?LinkId=2066588"
                        target="_blank"
                        rel="noopener noreferrer">
                        {VCResources.LearnMore}
                    </Link>
                </MessageBar>
            </div>
        );
    }

    private _renderMergeTitleControls(): JSX.Element {
        return (
            <TextField
                key="mergeTitle"
                disabled={this.state.mergeStrategy == GitPullRequestMergeStrategy.Rebase}
                className="vc-dialog-complete-merge-title-input"
                placeholder={VCResources.PullRequest_CompleteMergeDialog_MergeTitleWatermark}
                ariaLabel={VCResources.PullRequest_CompleteMergeDialog_MergeTitleTitle}
                maxLength={450}
                value={this.state.mergeTitle}
                onChanged={this._onMergeTitleChange} />
        );
    }

    private _renderMergeDetailsControls(): JSX.Element {
        return (
            <TextField
                key="mergeDetails"
                disabled={this.state.mergeStrategy == GitPullRequestMergeStrategy.Rebase}
                placeholder={VCResources.PullRequest_CompleteMergeDialog_MergeDetailsWatermark}
                ariaLabel={VCResources.PullRequest_CompleteMergeDialog_MergeDetailsTitle}
                rows={8}
                multiline
                errorMessage={this.state.mergeDescriptionError}
                resizable={false}
                value={this.state.mergeDescription}
                onChanged={this._onMergeDescriptionChange} />
        );
    }

    private _renderRebaseCommitMessageWarningControls(): JSX.Element {
        return (this.state.mergeStrategy == GitPullRequestMergeStrategy.Rebase &&
            <div
                key="rebaseCommitMsg"
                className="vc-dialog-complete-merge-rebase-commitmsg"
            >{VCResources.PullRequest_CompleteMergeDialog_RebaseCommitMessage}</div>
        );
    }

    private _renderTransitionWorkItemsControls(): JSX.Element {
        return (this.props.transitionWorkItemsIsEnabled &&
            <div key="transitionWorkItems" className={"vc-merge-dialog-line"}>
                <Checkbox
                    id="transitionWorkItemsCheckbox"
                    disabled={!this.state.canTransitionWorkItems}
                    label={VCResources.PullRequest_CompleteMergeDialog_TransitionWorkItemsCheckboxLabelText}
                    onChange={this._onTransitionWorkItemsChange}
                    checked={this.state.shouldTransitionWorkItems} />

                {this.state.transitionWorkItemsHint &&
                    <AdditionalInfoTooltip
                        key="hint"
                        id={getId("transitionWorkItemsCheckboxTooltip")}
                        content={this.state.transitionWorkItemsHint} />
                }
            </div>
        );
    }

    private _renderDeleteSourceControls(): JSX.Element {
        const isDeleteSourceBranchOptionEnabled: boolean = this.state.canDeleteSourceBranch;
        const deleteControlTitle = !isDeleteSourceBranchOptionEnabled ? this.state.cannotDeleteReasonHint : null;
        const deleteLabel = format(VCResources.PullRequest_CompleteMergeDialog_DeleteSourceCheckboxLabelText, this.state.sourceFriendlyName);

        return (
            <div key="deleteSource" className="vc-merge-dialog-line">
                <TooltipHost
                    content={deleteLabel}
                    directionalHint={DirectionalHint.bottomCenter}
                    overflowMode={TooltipOverflowMode.Parent}>
                    <Checkbox
                        id="deleteSourceBranchCheckbox"
                        disabled={!isDeleteSourceBranchOptionEnabled}
                        tooltipProps={deleteControlTitle ? { text: deleteControlTitle } : undefined}
                        onChange={this._onDeleteSourceBranchChange}
                        checked={this.state.shouldDeleteSourceBranch}
                        label={deleteLabel} />
                </TooltipHost>

                {isDeleteSourceBranchOptionEnabled ||
                    // Hint why you aren't able to delete source branch
                    <AdditionalInfoTooltip
                        key="hint"
                        id={getId("deleteSourceBranchCheckboxTooltip")}
                        content={this.state.cannotDeleteReasonHint} />
                }
            </div>
        );
    }

    private _renderSquashControls(): JSX.Element {
        // If only one option available, disable 
        const checkboxDisabled = this.state.someMergeStrategiesDisallowed && !this.state.shouldBypassPolicy;

        return (
            <div key="squash" className="vc-merge-dialog-line">
                <Checkbox
                    key="squashMergeCheckbox"
                    disabled={this.state.someMergeStrategiesDisallowed && !this.state.shouldBypassPolicy}
                    onChange={this._onSquashMergeChange}
                    checked={this.state.shouldSquashMerge}
                    label={VCResources.PullRequest_CompleteMergeDialog_SquashMergeCheckboxLabelText} />

                {checkboxDisabled &&
                    // Hint why you are being forced to squash
                    <AdditionalInfoTooltip
                        key="hint"
                        id={getId("squashMergeCheckboxTooltip")}
                        content={
                            this.state.shouldSquashMerge ?
                            VCResources.PullRequest_PolicyMergeStrategySquashRequired :
                            VCResources.PullRequest_PolicyMergeStrategySquashRequired}
                    />
                }

                {
                    // "Learn more" link
                    <Link
                        key="learnMore"
                        className={"vc-dialog-squash-merge-link"}
                        href="https://go.microsoft.com/fwlink/?LinkId=708720"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={VCResources.PullRequest_CompleteMergeDialog_SquashMergeLearnMoreText}>
                        {VCResources.PullRequest_CompleteMergeDialog_SquashMergeLink}
                    </Link>
                }
            </div>
        );
    }

    private _renderAdvancedMergeUi(): JSX.Element {
        return (
            <div className="vc-completion-type-section" key="advanced">
                { this._renderMergeStrategyDropdown() }
                { this._renderCompletionAnimation() }
            </div>
        );
    }

    private _renderMergeStrategyDropdown(): JSX.Element {
        return (
            <MergeStrategyDropdown
                key="mergeStrategy"
                initialSelectedMergeStrategy={this.state.mergeStrategy}
                mergePolicySetting={this.state.mergePolicySetting}
                canRebaseSourceBranch={this.state.canRebaseSourceBranch}
                cannotRebaseReasonHint={this.state.cannotRebaseReasonHint}
                canBypassPolicy={this.state.canBypassPolicy && !this.props.autoComplete}
                shouldBypassPolicy={this.state.shouldBypassPolicy}
                onChanged={this._onMergeStrategyChange} 
            />
        );
    }

    private _renderCompletionAnimation(): JSX.Element {
        return (
            <div
                className="vc-completion-animation-container"
                key="animContainer"
            >
                <span
                    tabIndex={0}
                    onKeyUp={this.restartAnimation}
                    onClick={this.restartAnimation}
                >
                    <CompletionAnimation
                        key="anim"
                        mergeStrategy={this.state.mergeStrategy}
                        sequence={this.state.animationSequence}
                    />
                </span>
            </div>
        );
    }

    private _renderBypassControls(): JSX.Element {
        return (this._arePolicyBypassControlsVisible &&
            <div key="bypass">
                <div className="section-header">{VCResources.PullRequest_CompleteMergeDialog_PolicyOverride}</div>
                <Checkbox
                    className={"vc-merge-dialog-line"}
                    id="byPassPolicyCheckbox"
                    onChange={this._onBypassPolicyChange}
                    checked={this.state.shouldBypassPolicy}
                    label={VCResources.PullRequest_CompleteMergeDialog_ByPassPolicyCheckboxLabelText}
                />
                {this.state.shouldBypassPolicy &&
                    <TextField
                        key="reason"
                        ref={this._focusBypassReasonIfNeeded}
                        className="vc-dialog-complete-merge-bypassReason-input"
                        type="text"
                        id="byPassPolicyReason"
                        onChanged={this._onBypassReasonChange}
                        maxLength={140}
                        onGetErrorMessage={this._onBypassReasonGetError}
                        validateOnFocusOut={true}
                        validateOnFocusIn={false}
                        validateOnLoad={false}
                        placeholder={VCResources.PullRequest_CompleteMergeDialog_BypassReasonWatermark}
                        ariaLabel={VCResources.PullRequest_CompleteMergeDialog_BypassReasonTitle}
                        value={this.state.bypassReason}
                    />
                }
            </div>
        );
    }

    private _focusBypassReasonIfNeeded = (ref: ITextField): void => {
        // If the user does something to enable policy bypass, but no reason is set, we move the focus to the bypass
        // reason control to emphasize that it's required.

        if (this.state.moveFocusToBypassReason) {
            ref.focus();
            this.setState({ moveFocusToBypassReason: false });
        }
    }

    protected get _arePolicyBypassControlsVisible(): boolean {
        return !!(
            !this.props.autoComplete // Autocomplete never offers bypass controls
            && this.state
            && this.state.canBypassPolicy // Don't show bypass controls unless user has bypass permission
            && this._isBypassNeededForCompletion // Don't show unless policy bypass is actually needed
        );
    }

    private get _isBypassNeededForCompletion(): boolean {
        // Bypass is needed if a policy is failing, or user has chosen a prohibited merge strategy
        return !!(this.state.policyWarning || !this.state.isSelectedMergeStrategyAllowed);
    }

    protected get _isCompleteButtonEnabled(): boolean {
        if (!this.state || this.state.loading) {
            return false;
        }

        // disable Complete button if merge description is too long
        if (this.state.mergeDescriptionError) {
            return false;
        }

        // If user is setting autocomplete, don't worry about any policy issues unless an invalid merge strategy is selected
        // (waiting for failing policies is the whole point of autocomplete)
        if (this.props.autoComplete) {
            return this.state.isSelectedMergeStrategyAllowed;
        }

        // Disable Complete button if user needs bypass and any of these is true:
        // - Can't bypass (doesn't have permission), or
        // - Hasn't checked bypass, or
        // - Didn't enter a bypass reason
        if (this._isBypassNeededForCompletion
            && (
                !this.state.canBypassPolicy
                || !this.state.shouldBypassPolicy
                || !this.state.bypassReason
                || this.state.bypassReason.length < 1
            )
        ) {
            return false;
        }

        return true;
    }

    public componentDidMount(): void {
        Flux.instance().storesHub.relatedWorkItemsStore.addChangedListener(this._onChange);
        Flux.instance().storesHub.pullRequestDetailStore.addChangedListener(this._onChange);
        Flux.instance().storesHub.clientPolicyEvaluationStore.addChangedListener(this._onChange);
        Flux.instance().storesHub.pullRequestCompletionOptionsStore.addChangedListener(this._onChange);
    }

    public componentWillUnmount(): void {
        Flux.instance().storesHub.relatedWorkItemsStore.removeChangedListener(this._onChange);
        Flux.instance().storesHub.pullRequestDetailStore.removeChangedListener(this._onChange);
        Flux.instance().storesHub.clientPolicyEvaluationStore.removeChangedListener(this._onChange);
        Flux.instance().storesHub.pullRequestCompletionOptionsStore.removeChangedListener(this._onChange);
    }

    protected _onSave = (): void => {
        if (!this._isCompleteButtonEnabled) {
            return;
        }

        Flux.instance().actionCreator.completeMergeActionCreator.publishTelemetry();

        // When completing a pull request, make sure we write the uesr's preferences to the registry
        // so that we capture that they've used the default and can change defaults in the future without
        // impacting users who are already used to the previous default.
        Flux.instance().actionCreator.userPreferenceActionCreator.forceUpdatePreferences();

        this.props.onResult(this.state.completionOptions);
    }

    protected _onCancel = (): void => {
        this.props.onResult();
    }

    protected get _completeButtonText(): string {
        let buttonLabel = VCResources.PullRequest_CompleteMergeDialog_OkText;

        if (this._arePolicyBypassControlsVisible) {
            buttonLabel = VCResources.PullRequest_CompleteMergeDialog_OverrideText;
        }
        else if (this.props.autoComplete) {
            buttonLabel = VCResources.AutoComplete;
        }

        return buttonLabel;
    }

    private _onChange = (): void => {
        this.setState(this._getState());
    }

    private _incrementAnimationSequence = (): void => {
        const animationSequence =
            ((this.state && this.state.animationSequence) || 1) + 1;

        this.setState({ animationSequence });
    }

    private _getState(): ICompleteMergeBaseState {
        const completeMergeStore = Flux.instance().storesHub.pullRequestCompletionOptionsStore;
        const mergePolicySetting = completeMergeStore.getMergePolicySetting();

        const mergeTitle = completeMergeStore.getMergeTitle();
        const mergeDescription = completeMergeStore.getMergeDescription();
        const completionOptions = completeMergeStore.getCompletionOptions();
        const mergeStrategy = completionOptions.mergeStrategy;
        const mergeDescriptionError = completeMergeStore.getMergeDescriptionError();
        const bypassReason = completeMergeStore.getBypassReason();
        const shouldDeleteSourceBranch = completionOptions.deleteSourceBranch;
        const shouldTransitionWorkItems = completionOptions.transitionWorkItems;
        const shouldSquashMerge = completionOptions.squashMerge;
        const canBypassPolicy = completeMergeStore.canBypassPolicy();
        const canTransitionWorkItems = completeMergeStore.canTransitionWorkItems();
        const shouldBypassPolicy = !this.props.autoComplete && completeMergeStore.shouldBypassPolicy();
        const canRebaseSourceBranch = completeMergeStore.canRebaseSourceBranch();
        const cannotRebaseReasonHint = completeMergeStore.getCannotRebaseReasonHint();
        const canDeleteSourceBranch = completeMergeStore.canDeleteSourceBranch();
        const cannotDeleteReasonHint = completeMergeStore.getCannotDeleteReasonHint();
        const sourceFriendlyName = completeMergeStore.getSourceFriendlyName();
        const sourceBranchWillNotBeMergedWarning = completeMergeStore.getSourceBranchWillNotBeMergedWarning();

        // If this is set, it means there is at least one merge strategy forbidden by policies
        let someMergeStrategiesDisallowed: boolean =
            !mergePolicySetting.allowNoFastForward || !mergePolicySetting.allowSquash;

        if (mergePolicySetting.advancedMergesEnabled) {
            // New merge types only "count" if using new merge UI
            someMergeStrategiesDisallowed = someMergeStrategiesDisallowed
                || !mergePolicySetting.allowRebase || !mergePolicySetting.allowRebaseMerge;
        }

        // Check if the user has selected a merge strategy policies allow (if not they could still bypass policies)
        const strategyDetails = mergeStrategyDetails[mergeStrategy];
        const isSelectedMergeStrategyAllowed = !strategyDetails || strategyDetails.isAllowedByPolicy(mergePolicySetting);

        const policyWarning = CompleteMergeBase.getPolicyWarning(Flux.instance().storesHub.clientPolicyEvaluationStore.state.clientPolicyEvaluations);
        const policiesLoading = Flux.instance().storesHub.clientPolicyEvaluationStore.isLoading();

        let workItemTransitionWarning: WorkItemTransitionWarning = null;
        if (!FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.WebAccessVersionControlDisableWITValidationWarning, false)) {
            workItemTransitionWarning = Flux.instance().storesHub.relatedWorkItemsStore.getWorkItemTransitionWarning();
        }

        const animationSequence = ((this.state && this.state.animationSequence) || 1) + 0;

        // Check for new hint based on FF or old behavior
        let transitionWorkItemsHint = null;
        if (FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.GitCommitMessageWITTransition, false) && canTransitionWorkItems) {
            transitionWorkItemsHint = format(VCResources.PullRequest_CompleteMergeDialog_TransitionWorkItemsHintCheckboxLabelText, VCResources.FixesKeywordExample);
        } else if (!canTransitionWorkItems) {
            transitionWorkItemsHint = VCResources.PullRequest_CompleteMergeDialog_TransitionWorkItemsDisabledCheckboxLabelText;
        }

        return {
            mergePolicySetting,
            policyWarning,
            shouldSquashMerge,
            sourceBranchWillNotBeMergedWarning,
            workItemTransitionWarning,
            shouldDeleteSourceBranch,
            shouldTransitionWorkItems,
            canTransitionWorkItems,
            mergeDescription,
            mergeDescriptionError,
            mergeTitle,
            loading:
                policiesLoading ||
                Flux.instance().storesHub.relatedWorkItemsStore.isLoading() ||
                Flux.instance().storesHub.pullRequestDetailStore.isLoading() ||
                Flux.instance().storesHub.pullRequestDetailStore.sourceRefStatusIsLoading() ||
                Flux.instance().storesHub.pullRequestCompletionOptionsStore.isLoading(),
            shouldBypassPolicy,
            canBypassPolicy,
            bypassReason,
            moveFocusToBypassReason: false,
            canRebaseSourceBranch,
            cannotRebaseReasonHint,
            canDeleteSourceBranch,
            cannotDeleteReasonHint,
            sourceFriendlyName,
            mergeStrategy,
            someMergeStrategiesDisallowed,
            isSelectedMergeStrategyAllowed,
            completionOptions,
            animationSequence,
            transitionWorkItemsHint: transitionWorkItemsHint,
        };
    }

    private restartAnimation = (ev?: (React.MouseEvent<HTMLDivElement> & React.KeyboardEvent<HTMLDivElement>)): void => {
        if (ev.keyCode !== undefined && ev.keyCode != 32) {
            // For keyboard events, only respond to space bar
            return;
        }

        this._incrementAnimationSequence();
        ev.preventDefault();
    }

    private _onSquashMergeChange = (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked?: boolean) => {
        Flux.instance().actionCreator.userPreferenceActionCreator.updateSquashMerge(isChecked);
        Flux.instance().actionCreator.userPreferenceActionCreator.updateMergeStrategy(
            (!!isChecked ? GitPullRequestMergeStrategy.Squash : GitPullRequestMergeStrategy.NoFastForward)
        );
    }

    private _onDeleteSourceBranchChange = (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked?: boolean): void => {
        Flux.instance().actionCreator.userPreferenceActionCreator.updateDeleteSourceBranch(isChecked);
    }

    private _onTransitionWorkItemsChange = (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, isChecked?: boolean): void => {
        Flux.instance().actionCreator.userPreferenceActionCreator.updateTransitionWorkItems(isChecked);
    }

    private _onMergeStrategyChange = (mergeStrategy: GitPullRequestMergeStrategy): void => {
        Flux.instance().actionCreator.userPreferenceActionCreator.updateMergeStrategy(mergeStrategy);
        Flux.instance().actionCreator.userPreferenceActionCreator.updateSquashMerge(
            (mergeStrategy == GitPullRequestMergeStrategy.Squash)
        );

        const strategyDetails = mergeStrategyDetails[mergeStrategy];
        const isMergeStrategyAllowedByPolicy = !strategyDetails || strategyDetails.isAllowedByPolicy(this.state.mergePolicySetting);

        // If the user selected a merge strategy which requires bypassing policy, force policy bypass on
        if (!isMergeStrategyAllowedByPolicy && !this.state.shouldBypassPolicy) {
            this._onBypassPolicyChange(null, true);
        }

        // On the other hand, if user switches to a merge strategy which is allowed and there's no other
        // policy blocking, turn bypass off
        if (isMergeStrategyAllowedByPolicy && !this.state.policyWarning && this.state.shouldBypassPolicy) {
            this._onBypassPolicyChange(null, false);
        }
    }

    private _onMergeDescriptionChange = (value): void => {
        Flux.instance().actionCreator.completeMergeActionCreator.updateMergeDescription(value);
    }

    private _onMergeTitleChange = (value): void => {
        Flux.instance().actionCreator.completeMergeActionCreator.updateMergeTitle(value);
    }

    private _onBypassPolicyChange = (ev?: React.FormEvent<HTMLElement>, isChecked?: boolean): void => {
        Flux.instance().actionCreator.completeMergeActionCreator.updateBypassPolicy(isChecked);

        if (isChecked && !this.state.bypassReason && !this.state.moveFocusToBypassReason) {
            this.setState({ moveFocusToBypassReason: true });
        }
    }

    private _onBypassReasonChange = (value: string): void => {
        Flux.instance().actionCreator.completeMergeActionCreator.updateBypassReason(value);
    }

    private _onBypassReasonGetError = (value): string => {
        if (!value) {
            return VCResources.PullRequest_Required;
        } else {
            return "";
        }
    }

    private static getPolicyWarning(clientPolicyEvaluations: ClientPolicyEvaluation[]): string {
        const rejectedPolicyEvaluations = clientPolicyEvaluations
            .filter(x => x.policyEvaluation.status !== PolicyEvaluationStatus.Approved && x.policyEvaluation.isBlocking);

        if (rejectedPolicyEvaluations.length === 0) {
            return null;
        }

        if (rejectedPolicyEvaluations.length === 1) {
            return rejectedPolicyEvaluations[0].policyEvaluation.displayText;
        }

        return format(VCResources.PullRequest_CompleteMergeDialog_ByPassPolicyWarning, rejectedPolicyEvaluations.length);
    }
}